import { LegalPage } from "./LegalPage";

export const PrivacyPolicy = () => {
  return (
    <LegalPage title="Privacy Policy" lastUpdated="June 4, 2026">
      <p className="legalUpdated">Effective as of June 4, 2026</p>

      <p>
        Sharecipe ("we", "our", or "us") is committed to protecting your
        privacy. This Privacy Policy explains how personal information is
        collected, used, stored, and shared when you use the Sharecipe mobile
        application, website, associated subdomains, and related services
        (collectively, the "Service" or the "Application").
      </p>
      <p>
        By accessing or using Sharecipe, you acknowledge that you have read and
        understood this Privacy Policy and our Terms of Use.
      </p>

      <h2>Definitions and Key Terms</h2>
      <ul>
        <li>
          <strong>Company, we, us, or our</strong> means AZ Developers LLC,
          registered at 21 Alimardan Topchubashov str., apt 82, Nasimi region,
          Baku AZ1009, Azerbaijan.
        </li>
        <li>
          <strong>Application or App</strong> means the Sharecipe mobile
          application and related services.
        </li>
        <li>
          <strong>Service</strong> means the service provided through Sharecipe,
          including recipes, user profiles, clubs, marketplace listings, allergy
          filtering, and related features.
        </li>
        <li>
          <strong>User or you</strong> means any person or entity that accesses,
          registers for, or uses Sharecipe.
        </li>
        <li>
          <strong>Vendor</strong> means a user who creates marketplace listings
          for food-related products or services.
        </li>
        <li>
          <strong>Marketplace</strong> means the listing-only area of Sharecipe
          where Vendors may display food-related products or services and Users
          may discover or contact Vendors.
        </li>
        <li>
          <strong>Content</strong> means recipes, photographs, comments,
          articles, marketplace listings, shop information, ingredients, and
          other materials submitted, uploaded, or published by Users.
        </li>
        <li>
          <strong>Personal Data</strong> means information that identifies,
          relates to, describes, or can reasonably be linked to an identified or
          identifiable person.
        </li>
        <li>
          <strong>Device</strong> means any internet-connected phone, tablet,
          computer, or other device used to access Sharecipe.
        </li>
        <li>
          <strong>IP address</strong> means a number assigned to a device
          connected to the internet, which may be used to estimate general
          location and provide security or technical functions.
        </li>
        <li>
          <strong>Third-party service</strong> means a service provider that
          helps us operate the Application, such as hosting, database, analytics,
          crash reporting, payment processing, application store, maps, customer
          support, security, and technical service providers.
        </li>
      </ul>

      <h2>What Information Do We Collect?</h2>
      <p>
        We may collect the following information when you create an account, use
        Sharecipe, publish Content, use the Marketplace, contact us, or use
        optional features of the Application:
      </p>
      <ul>
        <li>
          Account information, such as your name, username, email address, phone
          number, password or authentication information, and profile
          information.
        </li>
        <li>
          Content you submit, such as recipes, ingredients, photographs,
          comments, articles, marketplace listings, shop information, and other
          materials you upload or publish in the Application.
        </li>
        <li>
          Allergy and dietary preference information, if you choose to use the
          allergy filtering features of the Application. This may include
          allergens or food-related restrictions selected by you in the
          Application. Providing this information is optional, but some allergy
          filtering features may not work without it.
        </li>
        <li>
          Marketplace information, such as vendor profile details, shop page
          information, product or service listings, location area, and
          information needed to display nearby vendors and marketplace listings.
        </li>
        <li>
          Location information, if you allow location access. Location data is
          used only to help display nearby vendors, marketplace listings, and
          location-based marketplace features. Sharecipe does not use location
          data for advertising purposes.
        </li>
        <li>
          Photos and camera/gallery access, if you choose to grant such
          permissions. These permissions are used to allow you to upload images
          to recipes, posts, profiles, or marketplace listings. You may deny
          camera or gallery permissions, but some upload features may not work.
        </li>
        <li>
          Technical and usage information, such as device type, app version,
          operating system, log data, crash data, IP address, and general usage
          information needed to operate, secure, maintain, and improve the
          Application.
        </li>
        <li>
          Payment and subscription status information. Sharecipe does not collect
          or store full credit card numbers, debit card numbers, CVV codes, or
          full billing details. Payments are processed by third-party payment
          processors or application stores, such as Stripe for Android payments
          and Apple for iOS payments, depending on the platform and payment
          method used. We may receive limited payment-related information such as
          subscription status, transaction identifiers, receipts, renewal status,
          cancellation status, and payment confirmation data.
        </li>
        <li>
          Communications information, such as messages you send to us, support
          requests, feedback, and other information you choose to provide when
          contacting us.
        </li>
      </ul>
      <p>
        We do not collect full debit or credit card numbers directly through the
        Application.
      </p>

      <h2>Information From Third Parties</h2>
      <p>
        If you choose to sign in through a third-party account, such as Google,
        Facebook, Apple, or another supported sign-in provider, we may receive
        information permitted by that provider and authorized by you, such as
        your name, email address, profile image, account identifier, and
        authentication information.
      </p>
      <p>
        We may also receive limited information from payment processors,
        application stores, analytics or crash-reporting tools, security
        providers, and technical service providers as necessary to operate,
        protect, and support the Application.
      </p>

      <h2>Do We Share Information With Third Parties?</h2>
      <p>
        We do not sell users' personal information. We do not display advertising
        in the Application, and we do not share personal information for
        advertising purposes.
      </p>
      <p>
        We may share limited information with trusted service providers who help
        us operate, host, maintain, secure, analyze, and support the Application.
        These providers may include hosting providers, database providers,
        analytics and crash-reporting tools, customer support tools, payment
        processors, application stores, map services, and technical service
        providers.
      </p>
      <p>
        We share information only as necessary for these providers to perform
        services for us and subject to appropriate confidentiality and security
        obligations.
      </p>
      <p>
        We do not share allergy or dietary preference information with
        advertisers. We do not use allergy information, health-related
        information, or precise location data for advertising.
      </p>
      <p>
        We may disclose information if required by law, legal process, court
        order, governmental request, or where we believe disclosure is necessary
        to protect the rights, safety, or security of Sharecipe, our users, or
        others.
      </p>
      <p>
        We may also transfer information in connection with a merger,
        acquisition, restructuring, sale of assets, or similar business
        transaction, provided that the recipient is required to protect the
        information in accordance with this Privacy Policy or applicable law.
      </p>

      <h2>How Do We Use The Information We Collect?</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>create, manage, and secure your account;</li>
        <li>
          provide the main features of the Application, including recipes, clubs,
          allergy filtering, profiles, marketplace listings, and vendor
          discovery;
        </li>
        <li>
          display nearby vendors and marketplace listings when location-based
          marketplace features are used;
        </li>
        <li>
          process and manage subscriptions, account status, and access to paid
          features;
        </li>
        <li>
          allow users to upload, publish, edit, view, and interact with Content;
        </li>
        <li>
          respond to support requests and communicate with users about their
          accounts;
        </li>
        <li>
          send service, account, security, payment, subscription, and
          administrative messages;
        </li>
        <li>
          send optional product updates or marketing communications where
          permitted by law and where you have not opted out;
        </li>
        <li>improve, maintain, troubleshoot, and secure the Application;</li>
        <li>
          detect, prevent, and respond to fraud, abuse, spam, harmful activity,
          or violations of our Terms of Use;
        </li>
        <li>
          comply with legal obligations, enforce our rights, and respond to
          lawful requests.
        </li>
      </ul>
      <p>
        Allergy and dietary preference information is used only to provide
        allergy-related and food-filtering features selected by the user. We do
        not use allergy information for advertising.
      </p>

      <h2>Email Communications</h2>
      <p>
        We may use your email address to send account-related messages, security
        notices, subscription or payment notices, support responses, updates
        about the Application, and other service communications.
      </p>
      <p>
        We may also send optional promotional or product-update emails where
        permitted by law. You may unsubscribe from optional marketing emails at
        any time by using the unsubscribe link in the email or by contacting us.
        Even if you opt out of optional marketing messages, we may still send
        non-marketing service messages related to your account, security, legal
        notices, subscriptions, or use of the Application.
      </p>

      <h2>Location Data</h2>
      <p>
        Location access is optional. If you grant location permission, Sharecipe
        uses your location only to show nearby vendors, marketplace listings, and
        other location-based marketplace features.
      </p>
      <p>
        We do not use your GPS location to create advertising profiles, target
        advertisements, or sell advertising. You can disable location access
        through your device settings. If you disable location access, some
        marketplace features may be unavailable or less accurate.
      </p>

      <h2>Payments and Billing Information</h2>
      <p>
        Sharecipe does not collect or store full credit card numbers, debit card
        numbers, CVV codes, or full billing details.
      </p>
      <p>
        Payments and subscriptions are processed by third-party payment
        processors or application stores, such as Stripe for Android payments and
        Apple for iOS payments, depending on the platform and payment method
        used. These third parties process payment information under their own
        terms and privacy policies.
      </p>
      <p>
        Sharecipe may receive limited payment-related information necessary to
        manage your account and subscription, such as transaction identifiers,
        subscription status, renewal status, cancellation status, receipt data,
        and payment confirmation. We use this information only to provide access
        to paid features, manage subscriptions, provide support, and comply with
        legal or accounting obligations.
      </p>

      <h2>How Long Do We Keep Your Information?</h2>
      <p>
        We keep personal information only for as long as reasonably necessary to
        provide the Application, maintain your account, comply with legal
        obligations, resolve disputes, enforce our agreements, prevent abuse, and
        maintain security.
      </p>
      <p>In general:</p>
      <ul>
        <li>Account information is kept while your account remains active.</li>
        <li>
          Content you publish may remain available until you delete it, remove
          your account, or we remove it under our Terms of Use.
        </li>
        <li>
          Marketplace listings may remain available while they are active and may
          be retained for a reasonable period after removal for safety, dispute,
          moderation, or legal purposes.
        </li>
        <li>
          Allergy and dietary preference information is kept while you use those
          features or until you delete or update it.
        </li>
        <li>
          Location information is used for marketplace functionality and is not
          kept longer than reasonably necessary for that purpose, unless required
          for security, fraud prevention, legal compliance, or troubleshooting.
        </li>
        <li>
          Payment and subscription records may be kept as required for
          accounting, tax, legal, fraud prevention, and subscription management
          purposes.
        </li>
        <li>
          Support communications may be kept for a reasonable period to help us
          respond to requests, resolve issues, and keep records of our
          communications.
        </li>
        <li>
          Backup copies may remain for a limited period before being deleted or
          overwritten in the ordinary course of business.
        </li>
      </ul>
      <p>
        When we no longer need personal information, we will delete it, anonymize
        it, or securely retain it only where required by law or for legitimate
        business purposes.
      </p>

      <h2>How Do We Protect Your Information?</h2>
      <p>
        We use reasonable administrative, technical, and organizational measures
        designed to protect personal information against unauthorized access,
        disclosure, alteration, loss, misuse, or destruction. These measures may
        include access controls, encryption where appropriate, secure hosting
        practices, monitoring, and other safeguards appropriate to the nature of
        the information and the risks involved.
      </p>
      <p>
        Because no method of transmission or storage is completely secure, we
        cannot guarantee absolute security. You are responsible for keeping your
        password and account credentials confidential and for notifying us if you
        believe your account has been compromised.
      </p>

      <h2>International Transfers</h2>
      <p>
        Sharecipe is incorporated in Azerbaijan. Information collected through the
        Application may be stored, processed, or transferred in Azerbaijan or
        other countries where we or our service providers operate. These
        countries may have data protection laws that differ from those in your
        country. Where required by applicable law, we take steps designed to
        protect personal information transferred internationally.
      </p>

      <h2>Your Choices and Rights</h2>
      <p>
        Depending on your location and applicable law, you may have rights to
        access, correct, update, delete, restrict, object to, or request a copy
        of your personal information. You may also have the right to withdraw
        consent where processing is based on consent.
      </p>
      <p>
        You can update certain account information through the Application. You
        can disable camera, photo gallery, or location permissions through your
        device settings. You can unsubscribe from optional marketing emails by
        using the unsubscribe link or contacting us.
      </p>
      <p>
        To exercise privacy rights or make a request, contact us at{" "}
        <a href="mailto:info@sharecipe.net">info@sharecipe.net</a>. We may ask you
        to verify your identity before fulfilling a request. Some information may
        be retained where necessary for legal, security, fraud prevention,
        dispute resolution, backup, or legitimate business purposes.
      </p>

      <h2>Personnel</h2>
      <p>
        If you are a Sharecipe worker or applicant, we may collect information you
        voluntarily provide to us for recruitment, employment, contract
        management, administrative, and legal purposes. You may contact us to
        request access, correction, or deletion of this information where
        permitted by applicable law.
      </p>

      <h2>Sale of Business and Affiliates</h2>
      <p>
        We may disclose or transfer information in connection with a merger,
        acquisition, restructuring, financing, bankruptcy, sale of assets, or
        similar business transaction. We may also share information with corporate
        affiliates where necessary to operate, support, or protect Sharecipe,
        provided that such affiliates handle the information consistently with
        this Privacy Policy or applicable law.
      </p>

      <h2>Cookies and Similar Technologies</h2>
      <p>
        Sharecipe and its website may use cookies, local storage, sessions, and
        similar technologies to remember preferences, support login sessions,
        improve functionality, maintain security, understand usage, and operate
        the Service. You can adjust browser settings to block or delete cookies,
        but some features may not work correctly if cookies or similar
        technologies are disabled.
      </p>
      <p>
        We do not place full payment card details in cookies or local storage.
      </p>

      <h2>Tracking Technologies and Third-Party Tools</h2>
      <ul>
        <li>
          Google Maps API or similar map services may be used to provide
          location-based marketplace features, maps, and vendor discovery. Such
          services may process information according to their own privacy
          policies.
        </li>
        <li>
          Local storage may be used to store app or website preferences, session
          data, and other client-side data needed for functionality.
        </li>
        <li>
          Sessions may be used to identify logged-in users and maintain secure
          access to the Service.
        </li>
        <li>
          Analytics and crash-reporting tools may be used to understand app
          performance, fix errors, maintain security, and improve the Service.
        </li>
      </ul>

      <h2>Third-Party Services and Links</h2>
      <p>
        The Application may contain links to or integrations with third-party
        websites, services, application stores, payment processors, map
        providers, or other services. We are not responsible for the privacy
        practices, terms, content, accuracy, quality, legality, or security of
        third-party services. Your use of third-party services is subject to
        their own terms and privacy policies.
      </p>

      <h2>Children's Privacy</h2>
      <p>
        Sharecipe is not directed to children under the age of 16. We do not
        knowingly collect personal information from anyone under the age of 16
        without appropriate consent where required by law. If you are a parent or
        guardian and believe that a child has provided us with personal
        information, please contact us. If we become aware that we have collected
        personal information from a child in violation of applicable law, we will
        take reasonable steps to delete it.
      </p>

      <h2>Information for Users in the European Economic Area and United Kingdom</h2>
      <p>
        If you are located in the European Economic Area or United Kingdom,
        applicable data protection laws may give you additional rights, including
        the right to access, correct, delete, restrict, object to, or request
        portability of your personal data. You may also have the right to lodge a
        complaint with a data protection authority.
      </p>
      <p>
        Where required, we process personal data based on one or more legal bases,
        such as performance of a contract, consent, legitimate interests,
        compliance with legal obligations, or protection of vital interests.
        Allergy and dietary preference information is optional and used only to
        provide the allergy and food-filtering features selected by the user.
      </p>

      <h2>California Residents</h2>
      <p>
        California residents may have rights to know, access, correct, delete, or
        limit certain uses of personal information, and to opt out of the sale or
        sharing of personal information where applicable. Sharecipe does not sell
        personal information and does not share personal information for
        cross-context behavioral advertising.
      </p>
      <p>
        To exercise applicable California privacy rights, contact us at{" "}
        <a href="mailto:info@sharecipe.net">info@sharecipe.net</a>. We will not
        discriminate against you for exercising privacy rights.
      </p>

      <h2>Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time to reflect changes to
        the Application, our practices, legal requirements, or other reasons. When
        we make material changes, we will provide notice as required by applicable
        law, such as through the Application or by other reasonable means.
        Continued use of the Application after an updated Privacy Policy becomes
        effective means you acknowledge the updated policy.
      </p>

      <h2>Governing Law</h2>
      <p>
        This Privacy Policy is governed by the laws of Azerbaijan, without regard
        to conflict of law principles. Your use of the Application may also be
        subject to other local, national, or international laws depending on your
        location.
      </p>

      <h2>Contact Us</h2>
      <p>
        If you have any questions about this Privacy Policy or how Sharecipe
        handles personal information, please contact us:
      </p>
      <ul>
        <li>
          Email: <a href="mailto:info@sharecipe.net">info@sharecipe.net</a>
        </li>
        <li>
          Website:{" "}
          <a href="https://www.sharecipe.net" target="_blank" rel="noreferrer">
            www.sharecipe.net
          </a>
        </li>
      </ul>
    </LegalPage>
  );
};
